/**
 * @name useDataIntegrityChecker
 * @description Hook para verificar integridade dos dados após identificação do aluno
 * Implementa o requisito TC05 - Sincronização de dados
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/hooks/useDataIntegrityChecker.ts
 */

import { useCallback } from "react";
import { useCarrinho } from "../contexts/CarrinhoContext";
import { useProdutoContext } from "../contexts/ProdutoContext";
import { useUserContext } from "../contexts/UserContext";
import { useAlert } from "./useAlert";

interface DataIntegrityCheck {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export const useDataIntegrityChecker = () => {
  const { showError, showWarning } = useAlert();
  const { alunoAtual } = useUserContext();
  const { categorias, produtos, isCarregandoProdutos } = useProdutoContext();
  const { limparCarrinho } = useCarrinho();

  /**
   * Verifica integridade completa dos dados após identificação do aluno
   */
  const verificarIntegridadeDados =
    useCallback(async (): Promise<DataIntegrityCheck> => {
      const errors: string[] = [];
      const warnings: string[] = [];

      console.log("🔍 Iniciando verificação de integridade dos dados...");

      // 1. Verificar se o aluno está identificado
      if (!alunoAtual) {
        errors.push("Aluno não identificado");
        return { isValid: false, errors, warnings };
      }

      console.log("✅ Aluno identificado:", alunoAtual.nome);

      // 2. Verificar saldo do aluno (TC01)
      if (alunoAtual.saldo === undefined || alunoAtual.saldo === null) {
        errors.push("Saldo do aluno não foi carregado");
      } else if (alunoAtual.saldo < 0) {
        warnings.push("Aluno possui saldo negativo");
      } else if (alunoAtual.saldo === 0) {
        warnings.push("Aluno não possui saldo disponível");
      }

      console.log("💰 Saldo verificado:", alunoAtual.saldo);

      // 3. Verificar carregamento de categorias (TC02)
      if (categorias.length === 0) {
        errors.push("Nenhuma categoria de produto foi carregada");
      } else {
        console.log(`✅ ${categorias.length} categorias carregadas`);
      }

      // 4. Verificar carregamento de produtos (TC03)
      if (isCarregandoProdutos) {
        warnings.push("Produtos ainda estão sendo carregados");
      } else if (produtos.length === 0) {
        errors.push("Nenhum produto foi carregado");
      } else {
        console.log(`✅ ${produtos.length} produtos carregados`);

        // Verificar se há produtos disponíveis
        const produtosDisponiveis = produtos.filter((p) => p.disponivel);
        if (produtosDisponiveis.length === 0) {
          warnings.push("Nenhum produto está disponível para compra");
        } else {
          console.log(`✅ ${produtosDisponiveis.length} produtos disponíveis`);
        }
      }

      // 5. Verificar organização
      if (!alunoAtual.organizacao_id) {
        errors.push("Organização do aluno não identificada");
      }

      // 6. Limpar carrinho se houver erros críticos
      if (errors.length > 0) {
        console.log("🧹 Limpando carrinho devido a erros críticos");
        limparCarrinho();
      }

      const isValid = errors.length === 0;

      // Exibir mensagens para o usuário
      if (errors.length > 0) {
        errors.forEach((error) => showError(error));
      }

      if (warnings.length > 0) {
        warnings.forEach((warning) => showWarning(warning));
      }

      console.log("📋 Verificação de integridade concluída:", {
        isValid,
        errorsCount: errors.length,
        warningsCount: warnings.length,
      });

      return { isValid, errors, warnings };
    }, [
      alunoAtual,
      categorias,
      produtos,
      isCarregandoProdutos,
      showError,
      showWarning,
      limparCarrinho,
    ]);

  /**
   * Forçar recarregamento de dados em caso de inconsistência
   */
  const forcarRecarregamentoDados = useCallback(async () => {
    console.log("🔄 Forçando recarregamento de dados...");

    try {
      // Aqui você pode implementar lógica para recarregar dados
      // Por exemplo, disparar novamente os carregamentos nos contextos
      window.location.reload(); // Solução drástica, mas efetiva
    } catch (error) {
      console.error("❌ Erro ao forçar recarregamento:", error);
      showError("Erro ao recarregar dados");
    }
  }, [showError]);

  return {
    verificarIntegridadeDados,
    forcarRecarregamentoDados,
  };
};

export default useDataIntegrityChecker;
